'use client';

/**
 * TokenFileForm — load a single persona from a local file, in parallel to
 * MagicLinkForm and the dev-persona switcher.
 *
 * The distribution model this supports: instead of one shared dev_tokens.json
 * with every persona (which anyone can browse), each person gets handed
 * exactly one small file — a single identity token, out-of-band (email,
 * shared password-manager vault, whatever). They never see a roster of who
 * else has access; the file IS their access. No wallet, no merge, no
 * persisted collection — just "load this one file."
 *
 * Two file shapes accepted:
 *   - JSON: a single DevUser-shaped object (or {users:[one entry]}) —
 *     {name, email, role, scope, requires_login, token}.
 *   - Plain text: just the raw JWT. Its own claims (email/role/scope) are
 *     decoded client-side (display only, no signature check) to build a
 *     label. Defaults to requires_login=true — a bare token from a file is
 *     treated as an identity token that must go through the real /auth/login
 *     exchange, never as a pre-scoped bypass, unless the JSON form says
 *     otherwise explicitly.
 *
 * Reuses useDevUserStore.selectUser() — the exact same activation path
 * (api.login() for identity tokens, api.setToken() for bypass) the existing
 * persona list already uses. No new store logic.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain auth
 */

import { useRef, useState } from 'react';
import { useDevUserStore } from '@/lib/stores/dev-user-store';
import { parseTokenFile } from '@/lib/auth/parse-token-file';

export function TokenFileForm() {
  const selectUser = useDevUserStore((s) => s.selectUser);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    setError(null);
    setLoading(true);
    try {
      const text = await file.text();
      const user = parseTokenFile(text);
      if (!user) {
        setError('Could not read a token from that file');
        return;
      }
      await selectUser(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load token file');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.txt,application/json,text/plain"
        onChange={onFileSelected}
        className="hidden"
        disabled={loading}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={loading}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm hover:bg-accent/50 disabled:opacity-50 transition-colors"
      >
        {loading ? 'Loading...' : 'Load a token file'}
      </button>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
